import React, { useEffect, useState, useCallback } from 'react';
import { sarAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { StatCard } from '../../components/import/ImportPrimitives';
import { ImportPicker } from './ImportPicker';
import { Scale, Loader2, ShieldCheck, CheckCircle2, AlertTriangle } from 'lucide-react';

export default function OvertimeParity() {
  const [imports, setImports] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    sarAPI.listImports(true)
      .then(({ data }) => {
        const imps = data.imports || [];
        setImports(imps);
        const preferred = imps.find((i) => i.is_final) || imps[0];
        if (preferred) setSelectedId(preferred.id);
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Could not load imports'));
  }, []);

  const run = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await sarAPI.overtimeParity(id);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not compute overtime parity');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedId) run(selectedId); }, [selectedId, run]);

  const s = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="sar-overtime-tab">
      <Alert className="border-sky-200 bg-sky-50" data-testid="sar-overtime-readonly">
        <ShieldCheck className="h-4 w-4 text-sky-600" />
        <AlertDescription className="text-sky-800 text-sm">
          <strong>Read-only parallel test.</strong> TriCorps recomputes weekly overtime from SAR paid
          hours (Sun–Sat week, 40-hour threshold) and lines it up against WinTeam's own overtime.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Scale className="w-5 h-5" /> Overtime parity</CardTitle>
          <CardDescription>Choose an import to compare WinTeam overtime against TriCorps-calculated overtime, per employee and week.</CardDescription>
        </CardHeader>
        <CardContent>
          <ImportPicker imports={imports} value={selectedId} onChange={setSelectedId} testId="sar-overtime-import-picker" />
        </CardContent>
      </Card>

      {loading && (
        <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="sar-overtime-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Computing parity…
        </div>
      )}

      {data && !loading && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard label="Employee-weeks" value={String(s.week_count ?? '—')} testId="sar-ot-stat-weeks" />
            <StatCard label="Employees" value={String(s.employee_count ?? '—')} testId="sar-ot-stat-emps" />
            <StatCard label="Matches" value={String(s.match_count ?? '—')} tone="text-emerald-700" testId="sar-ot-stat-match" />
            <StatCard label="Mismatches" value={String(s.mismatch_count ?? '—')} tone="text-amber-700" testId="sar-ot-stat-mismatch" />
          </div>

          <Card data-testid="sar-overtime-results">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Weekly overtime by employee</CardTitle></CardHeader>
            <CardContent className="pt-0">
              {(data.weeks || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center">No rows in this import.</p>
              ) : (
                <div className="overflow-auto max-h-[560px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Week</TableHead>
                        <TableHead className="text-right">Paid hrs</TableHead>
                        <TableHead className="text-right">TriCorps OT</TableHead>
                        <TableHead className="text-right">WinTeam OT</TableHead>
                        <TableHead className="text-right">Diff</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.weeks.map((w) => (
                        <TableRow key={`${w.employee_number}-${w.week_start}`} data-testid={`sar-ot-row-${w.employee_number}-${w.week_start}`} className={w.matches ? '' : 'bg-amber-50'}>
                          <TableCell className="font-mono text-xs">{w.employee_number}{w.employee_name ? ` · ${w.employee_name}` : ''}</TableCell>
                          <TableCell className="text-xs">{w.week_start} → {w.week_end}</TableCell>
                          <TableCell className="text-right">{w.paid_hours}</TableCell>
                          <TableCell className="text-right font-medium">{w.tricorps_ot_hours}</TableCell>
                          <TableCell className="text-right">{w.winteam_ot_hours}</TableCell>
                          <TableCell className="text-right font-medium">{w.difference}</TableCell>
                          <TableCell>
                            {w.matches ? (
                              <Badge variant="secondary" className="text-emerald-700 bg-emerald-50"><CheckCircle2 className="w-3 h-3 mr-1" />Match</Badge>
                            ) : (
                              <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Differs</Badge>
                            )}
                            {w.fully_salaried && <Badge variant="outline" className="ml-1 text-[10px]">Salaried</Badge>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
